import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Video, X } from "lucide-react";
import { http, fmtErr } from "../lib/api";

const ALLOWED = [".mp4", ".mov", ".webm", ".m4v"];
const MAX_MB = 100;

export function ReelUpload({ onUploaded }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [phase, setPhase] = useState(""); // "uploading" | "transcoding" | ""
  const inputRef = useRef(null);

  const validate = (f) => {
    const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED.includes(ext)) {
      toast.error(`Unsupported file. Allowed: ${ALLOWED.join(", ")}`);
      return false;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_MB} MB.`);
      return false;
    }
    return true;
  };

  const pick = (f) => {
    if (!f) return;
    if (!validate(f)) return;
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim());
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pick(f);
  };

  const reset = () => {
    setFile(null);
    setTitle("");
    setAuthor("");
    setProgress(0);
    setPhase("");
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Pick a video file first"); return; }
    if (!title.trim()) { toast.error("Please add a title"); return; }
    const form = new FormData();
    form.append("file", file);
    form.append("title", title.trim());
    if (author.trim()) form.append("author", author.trim());
    setUploading(true);
    setPhase("uploading");
    setProgress(0);
    try {
      const { data } = await http.post("/admin/reels/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 240000,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded * 100) / evt.total);
          setProgress(pct);
          if (pct >= 100) setPhase("transcoding");
        },
      });
      toast.success("Reel uploaded & transcoded 🎬");
      reset();
      onUploaded?.(data);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message || "Upload failed");
      setUploading(false);
      setPhase("");
    }
  };

  const input = "w-full bg-white border border-[#E5D8C1] px-4 py-3 focus:outline-none focus:border-[#D32F2F]";

  return (
    <form onSubmit={upload} className="bg-[#FDF4E3] border border-[#E5D8C1] p-6" data-testid="admin-reel-upload-form">
      <div className="flex items-center gap-2 mb-4">
        <Video size={20} className="text-[#D32F2F]" />
        <div className="font-heading text-xl uppercase tracking-wide">Upload a reel · auto-transcode</div>
      </div>
      <p className="text-sm text-[#5C5A56] mb-4">
        Drop an MP4/MOV/WEBM (max {MAX_MB} MB). We&apos;ll transcode to H.264 Constrained Baseline + generate a poster automatically. Once uploaded, the reel appears on the homepage instantly.
      </p>

      {!file ? (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`cursor-pointer border-2 border-dashed p-10 text-center transition-colors ${dragging ? "border-[#D32F2F] bg-[#D32F2F]/5" : "border-[#E5D8C1] hover:border-[#1A1A1A] bg-white"}`}
          data-testid="admin-reel-dropzone"
        >
          <Upload size={40} className="mx-auto mb-3 text-[#D32F2F]" />
          <div className="font-heading text-lg uppercase tracking-wide">Drop your MP4 here</div>
          <div className="text-xs text-[#5C5A56] mt-1 uppercase tracking-widest">or click to choose a file</div>
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED.join(",") + ",video/*"}
            onChange={(e) => pick(e.target.files?.[0])}
            className="hidden"
            data-testid="admin-reel-file-input"
          />
        </div>
      ) : (
        <div className="border border-[#E5D8C1] bg-white p-4 flex items-center gap-4" data-testid="admin-reel-file-selected">
          <div className="w-14 h-14 bg-[#1A1A1A] text-[#FFB300] flex items-center justify-center shrink-0">
            <Video size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-sm uppercase tracking-wide truncate">{file.name}</div>
            <div className="text-xs text-[#5C5A56] mt-0.5">{(file.size / (1024 * 1024)).toFixed(1)} MB</div>
          </div>
          {!uploading && (
            <button type="button" onClick={reset} className="text-[#5C5A56] hover:text-[#D32F2F]" data-testid="admin-reel-clear">
              <X size={18} />
            </button>
          )}
        </div>
      )}

      {file && (
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <label className="block"><span className="text-xs uppercase tracking-widest">Title *</span>
            <input required disabled={uploading} value={title} onChange={(e) => setTitle(e.target.value)} className={input + " mt-2"} placeholder="e.g. Extreme Ropes taste test" data-testid="admin-reel-title" />
          </label>
          <label className="block"><span className="text-xs uppercase tracking-widest">Author (optional)</span>
            <input disabled={uploading} value={author} onChange={(e) => setAuthor(e.target.value)} className={input + " mt-2"} placeholder="@handle" data-testid="admin-reel-author" />
          </label>
        </div>
      )}

      {uploading && (
        <div className="mt-4" data-testid="admin-reel-progress">
          <div className="flex justify-between text-xs uppercase tracking-widest text-[#5C5A56] mb-2">
            <span>{phase === "transcoding" ? "Transcoding to H.264…" : "Uploading…"}</span>
            <span>{phase === "transcoding" ? "" : `${progress}%`}</span>
          </div>
          <div className="w-full bg-white border border-[#E5D8C1] h-2 overflow-hidden">
            <div
              className={`h-full transition-all ${phase === "transcoding" ? "bg-[#FFB300] animate-pulse w-full" : "bg-[#D32F2F]"}`}
              style={phase === "transcoding" ? {} : { width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {file && (
        <div className="mt-4">
          <button
            type="submit"
            disabled={uploading}
            className="bg-[#D32F2F] text-white hover:bg-[#B71C1C] px-8 py-3 uppercase tracking-widest font-heading disabled:opacity-60 flex items-center gap-2"
            data-testid="admin-reel-submit"
          >
            {uploading && <Loader2 className="animate-spin" size={16} />}
            {uploading ? (phase === "transcoding" ? "Transcoding…" : "Uploading…") : "Upload & publish reel"}
          </button>
        </div>
      )}
    </form>
  );
}
